import { colors } from '@mm/utils';
import classNames from 'classnames';
import { isNil } from 'lodash';
import React from 'react';
import { HorizontalBar, Line } from 'react-chartjs-2';
import { Box } from '../Box/Box';
import { Circle } from '../Circle/Circle';
import { Heading } from '../Heading/Heading';
import { HelpText } from '../HelpText/HelpText';
import './Chart.pcss';

export type ChartData = {
  labels: string[];
  data: (number | string)[][];
  title?: string[];
  color: string[];
  gradientColor?: string[];
  types?: string[];
  withoutGradient?: boolean;
};

type TooltipsCallbacksType = {
  afterBody?: (
    items: {
      index: number;
    }[],
  ) => string;
  label?: (
    item: { datasetIndex: number; value: number },
    { datasets }: { datasets: { label: string }[] },
  ) => string;
};
type AdditionalOptsType = {
  showValuesForHorizontalBar?: boolean;
  yBeginAtZero?: boolean;
  min?: number;
  stacked?: boolean;
  borderDashXAxis?: [number, number];
  borderDashYAxis?: [number, number];
};

interface Props {
  className?: string;
  name?: string;
  headerRightSide?: string;
  subheader?: string;
  data?: ChartData;
  customLegend?: React.ReactNode;
  tooltipsCallbacks?: TooltipsCallbacksType;
  ticksCallback?: (value: string) => any;
  type?: string;
  additionalOpts?: AdditionalOptsType;
}

const getChartOpts = (
  tooltipsCallbacks?: TooltipsCallbacksType,
  additionalOpts?: AdditionalOptsType,
  ticksCallback?: (value: string) => any,
) => {
  const callbacks = tooltipsCallbacks ?? {};
  const showValuesForHorizontalBar =
    additionalOpts?.showValuesForHorizontalBar && {
      events: false,
      animation: {
        duration: 1,
        onComplete: props => {
          const chartInstance = props.chart;
          const ctx = chartInstance.ctx;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          chartInstance.data.datasets.forEach((dataset, i) => {
            const meta = chartInstance.controller.getDatasetMeta(i);
            meta.data.forEach((bar, index) => {
              const data = dataset.data[index];
              if (data) {
                ctx.fillText(data, bar._model.x + 5, bar._model.y + 6); // eslint-disable-line no-underscore-dangle
              }
            });
          });
        },
      },
    };
  const yBeginAtZero = isNil(additionalOpts?.yBeginAtZero)
    ? true
    : additionalOpts?.yBeginAtZero;

  return {
    ...showValuesForHorizontalBar,
    maintainAspectRatio: false,
    legend: {
      display: false,
      // couldn't figure out how to properly style this legend leaving here config for future refs
      // position: 'top',
      // align: 'start',
      // labels: {
      //   usePointStyle: true,
      //   boxWidth: 4,
      // },
    },
    scaleShowValues: true,
    scales: {
      xAxes: [
        {
          barPercentage: 0.3,
          stacked: additionalOpts?.stacked,
          gridLines: {
            borderDash: additionalOpts?.borderDashXAxis || [10, 10],
            color: colors.grayLighter,
          },
          ticks: {
            beginAtZero: true,
            maxRotation: 0,
            minRotation: 0,
          },
        },
      ],
      yAxes: [
        {
          stacked: additionalOpts?.stacked,
          gridLines: {
            borderDash: additionalOpts?.borderDashYAxis || [10, 10],
            color: colors.grayLighter,
            // drawOnChartArea: false,
          },
          ticks: {
            min: additionalOpts?.min,
            beginAtZero: yBeginAtZero,
            maxTicksLimit: 5,
            autoSkip: false,
            callback: value => (ticksCallback ? ticksCallback?.(value) : value),
          },
        },
      ],
    },
    elements: {
      point: {
        radius: 2,
        hitRadius: 10,
        hoverRadius: 4,
        hoverBorderWidth: 3,
      },
    },
    tooltips: {
      mode: 'index',
      intersect: false,
      callbacks,
    },
  };
};

const mapData = (
  name?: string,
  { labels = [], data = [], title, color }: ChartData = {} as ChartData,
) => ({
  labels,
  datasets: data.map((source, index) => ({
    label: (title && title[index]) || name,
    borderColor: (color && color[index]) || color,
    data: source.map(value => {
      if (isNil(value)) {
        return 0;
      }
      return value;
    }),
    pointHoverBackgroundColor: (color && color[index]) || color,
    borderWidth: 2,
    borderDashOffset: 0,
    pointBackgroundColor: '#fff',
    pointBorderWidth: 1,
    pointHitRadius: 10,
    pointHoverRadius: 5,
    pointRadius: 1,
  })),
});

export const Chart = ({
  name,
  headerRightSide,
  subheader,
  data,
  className,
  customLegend,
  tooltipsCallbacks,
  type,
  additionalOpts,
  ticksCallback,
}: Props) => {
  const dataSource = mapData(name, data);

  const patchDatasetFillColor = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');

    if (ctx) {
      const mappedWithGradient = dataSource.datasets.map((set, i) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(
          0,
          data?.gradientColor?.[i] || data?.color[i] || '#3a50f3',
        );

        if (!data?.withoutGradient) {
          gradient.addColorStop(1, '#FFFFFF');
        }
        const type = (data?.types && data?.types[i]) || 'line';
        const backgroundColor = data?.gradientColor?.[i] || gradient;
        return {
          ...set,
          fillColor: backgroundColor,
          backgroundColor,
          type,
        };
      });
      return {
        ...dataSource,
        datasets: mappedWithGradient,
      };
    }

    return dataSource;
  };

  return (
    <Box className={classNames(className, 'c-chart')}>
      {name && (
        <div className="c-chart__header">
          <div className="c-chart__header_subheader-container">
            <Heading className="c-chart__name" size={3} text={name} />
            {subheader && <div className="c-chart__subheader">{subheader}</div>}
          </div>
          {headerRightSide && (
            <div className="c-chart__header-right-side">{headerRightSide}</div>
          )}
        </div>
      )}
      {data && (
        <div className="c-chart__legend">
          {customLegend
            ? customLegend
            : data?.title?.map((v, i) => (
                <div className="c-chart__legend-item" key={v}>
                  <Circle
                    className="c-chart__legend-indicator"
                    color={data?.color[i]}
                  />
                  <HelpText className="c-chart__legend-label" text={v} />
                </div>
              ))}
        </div>
      )}
      <div className="c-chart__data">
        {type === 'horizontalBar' ? (
          <HorizontalBar
            data={patchDatasetFillColor}
            options={getChartOpts(
              tooltipsCallbacks,
              additionalOpts,
              ticksCallback,
            )}
            height={300}
          />
        ) : (
          <Line
            data={patchDatasetFillColor}
            options={getChartOpts(
              tooltipsCallbacks,
              additionalOpts,
              ticksCallback,
            )}
            height={300}
          />
        )}
      </div>
    </Box>
  );
};
